import { renderNotification, startLoading, stopLoading } from '../ReactComponents/common/notifications/toasts';

import AbortError from './AbortError';
import RequestError from './RequestError';

type ShowErrorToastResolver = (status: number) => boolean;
type GetErrorMessage<TErrorData> = (data: TErrorData | string) => string;

type Data = Document | XMLHttpRequestBodyInit;

const DEFAULT_OPTIONS = {
	csrfToken: true,
	json: true,
	loader: true,
	showErrorToast: () => true,
};

/**
 * Naformátuje data pro payload.
 *
 * @param   {object} data
 * @returns {string}
 */
function _formatFormData(data: Data) {
	return Object.entries(data)
		.map(
			([key, value]) =>
				// eslint-disable-next-line typescript-eslint/no-unsafe-argument
				`${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
		)
		.join('&');
}

interface RequestOptions<TErrorData> {
	abortSignal?: AbortSignal;
	csrfToken?: boolean; // Send CSRF token in header.
	json?: boolean; // Input data are serialized JSON, send them as JSON request.
	loader?: boolean; // Show loading toast.
	showErrorToast?: ShowErrorToastResolver; // Resolver which returns boolean, decides if error toast will be showed in case of error.
	getErrorMessage?: GetErrorMessage<TErrorData>; // Function to get message in case of error.
}

interface RequestParameter<TErrorData> {
	url: string;
	method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	data?: Data;
	options?: RequestOptions<TErrorData>;
}

type ErrorData = Record<string, unknown>;

/* eslint-disable typescript-eslint/no-unnecessary-type-parameters */
/**
 * Common module for all API calls.
 */

function _request<TData, TErrorData extends ErrorData>({
	url,
	method,
	data,
	options = DEFAULT_OPTIONS,
}: RequestParameter<TErrorData>): Promise<{ status: number; data: TData }> {
	/* eslint-enable typescript-eslint/no-unnecessary-type-parameters */
	// Assign defaults
	const { abortSignal, loader, json, csrfToken, showErrorToast, getErrorMessage } = {
		...DEFAULT_OPTIONS,
		...options,
	};

	let isLoading = loader;

	if (isLoading) {
		startLoading();
	}

	const finishLoading = () => {
		if (isLoading) {
			isLoading = false;
			stopLoading();
		}
	};

	return new Promise((resolve, reject) => {
		const request = new XMLHttpRequest();

		request.open(method, url, true);

		if (csrfToken && window.SSP.csrf_token) {
			request.setRequestHeader('X-CSRFToken', window.SSP.csrf_token);
		}

		function onLoad() {
			const { status } = request;

			finishLoading();

			if (status >= 200 && status < 300) {
				resolve({
					data:
						typeof request.response === 'string' && request.response
							? (JSON.parse(request.response) as TData)
							: ({} as TData),
					status,
				});

				return;
			}

			// ----- Error processing

			let parsedErrorResponse: TErrorData | undefined;
			let errorMessage = 'Request error';

			try {
				// eslint-disable-next-line typescript-eslint/no-unsafe-argument
				parsedErrorResponse = JSON.parse(request.response) as TErrorData;

				if (getErrorMessage) {
					errorMessage = getErrorMessage(parsedErrorResponse);
				} else if ('message' in parsedErrorResponse && typeof parsedErrorResponse['message'] === 'string') {
					errorMessage = parsedErrorResponse['message'];
				}
			} catch {
				console.error('Invalid parsing JSON response, received data: ', request.response);

				errorMessage = getErrorMessage?.(request.statusText) ?? request.statusText;
			}

			if (showErrorToast(status)) {
				renderNotification({ feel: 'error', title: errorMessage });
			}
			const error = new RequestError<TErrorData>({
				data: request.response,
				message: errorMessage,
				status,
			});

			reject(error);
		}

		function onError() {
			reject(new Error('Network error'));

			finishLoading();
		}

		request.onload = onLoad;
		request.onerror = onError;

		abortSignal?.addEventListener('abort', () => {
			finishLoading();
			request.abort();
			reject(new AbortError());
		});

		if (['POST', 'PUT', 'PATCH'].includes(method) && data) {
			if (json) {
				request.setRequestHeader('Content-type', 'application/json;charset=UTF-8');

				request.send(data);
			} else {
				if (data instanceof FormData) {
					request.send(data);
				} else {
					request.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');

					request.send(_formatFormData(data));
				}
			}

			return;
		}

		request.send();
	});
}

/* eslint-disable typescript-eslint/no-unnecessary-type-parameters */
export function POST<TData, TErrorData extends ErrorData = {}>(
	url: string,
	data?: Data,
	options?: RequestOptions<TErrorData>,
) {
	return _request<TData, TErrorData>({
		/* eslint-enable typescript-eslint/no-unnecessary-type-parameters */
		method: 'POST',
		url,
		...(data !== undefined && { data }),
		...(options && { options }),
	});
}

/* eslint-disable typescript-eslint/no-unnecessary-type-parameters */
export function GET<TData, TErrorData extends ErrorData = {}>(url: string, options?: RequestOptions<TErrorData>) {
	return _request<TData, TErrorData>({
		/* eslint-enable typescript-eslint/no-unnecessary-type-parameters */
		method: 'GET',
		url,
		...(options && { options }),
	});
}

/* eslint-disable typescript-eslint/no-unnecessary-type-parameters */
export function PUT<TData, TErrorData extends ErrorData = {}>(
	url: string,
	data?: Data,
	options?: RequestOptions<TErrorData>,
) {
	return _request<TData, TErrorData>({
		/* eslint-enable typescript-eslint/no-unnecessary-type-parameters */
		method: 'PUT',
		url,
		...(data !== undefined && { data }),
		...(options && { options }),
	});
}

/* eslint-disable typescript-eslint/no-unnecessary-type-parameters */
export function PATCH<TData, TErrorData extends ErrorData = {}>(
	url: string,
	data?: Data,
	options?: RequestOptions<TErrorData>,
) {
	return _request<TData, TErrorData>({
		/* eslint-enable typescript-eslint/no-unnecessary-type-parameters */
		method: 'PATCH',
		url,
		...(data !== undefined && { data }),
		...(options && { options }),
	});
}

/* eslint-disable typescript-eslint/no-unnecessary-type-parameters */
export function DELETE<TData, TErrorData extends ErrorData = {}>(url: string, options?: RequestOptions<TErrorData>) {
	return _request<TData, TErrorData>({
		/* eslint-enable typescript-eslint/no-unnecessary-type-parameters */
		method: 'DELETE',
		url,
		...(options && { options }),
	});
}
